import { getRunnerData, type RunnerStepContent } from '../api/campaignData';
import { STEPS, type StepDef, type StepKind } from './steps';

/**
 * DB-authoring seam for the six workshop steps. The bundled `STEPS` array defines the runner's
 * STRUCTURE (slug, kind, phase, order) and ships default copy; the portal injects the campaign's
 * DB-authored CONTENT for each step through `RunnerDataContext.stepContent` (from each frontier step's
 * `metadata.runner` + `metadata.helpLinks`). These helpers merge that content over the bundled
 * defaults so the runner renders authored copy while its structure stays fixed. With no portal context
 * (the standalone demo) or no entry for a kind, everything falls back to the bundled `StepDef` — so
 * this is a pure enhancement that never breaks the un-seeded case.
 *
 * Mirrors `resolveStakeholders` / `resolveReportSections` in `campaignData`: "DB when present, bundled
 * otherwise", read live from the module-singleton context.
 */

function contentFor(kind: StepKind): RunnerStepContent | undefined {
  return getRunnerData()?.stepContent?.find((c) => c.kind === kind);
}

/** Merge the campaign's DB-authored content over one bundled `StepDef` (structure stays bundled). */
export function resolveStepDef(base: StepDef): StepDef {
  const o = contentFor(base.kind);
  if (!o) return base;
  return {
    ...base,
    ...(o.tag != null ? { tag: o.tag } : {}),
    ...(o.title != null ? { title: o.title } : {}),
    ...(o.subtitle != null ? { subtitle: o.subtitle } : {}),
    ...(o.cta != null ? { cta: o.cta } : {}),
    ...(o.liveEligible != null ? { liveEligible: o.liveEligible } : {}),
    ...(o.helpLinks != null ? { helpLinks: o.helpLinks } : {}),
    ...(o.quickPrompts != null ? { quickPrompts: o.quickPrompts } : {}),
    ...(o.fileRefs != null ? { fileRefs: o.fileRefs } : {}),
    ...(o.questionRefs != null ? { questionRefs: o.questionRefs } : {}),
    ...(o.guideTitle != null ? { guideTitle: o.guideTitle } : {}),
    ...(o.guideBody != null ? { guideBody: o.guideBody } : {}),
  };
}

/** All steps with DB content merged, in bundled order. Safe to use structurally (identity of fields
 * that are never overridden — slug/kind/phase — is preserved). */
export function resolvedSteps(): StepDef[] {
  return STEPS.map(resolveStepDef);
}
